import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { ArrowLeft, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import jsPDF from "jspdf";
import { toast } from "sonner";

const Privacy = () => {
  const handleDownloadPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    const maxWidth = pageWidth - margin * 2;
    let yPos = 20;
    const lineHeight = 6;
    const sectionSpacing = 10;

    const addText = (text: string, fontSize: number = 10, isBold: boolean = false) => {
      doc.setFontSize(fontSize);
      doc.setFont("helvetica", isBold ? "bold" : "normal");
      const lines = doc.splitTextToSize(text, maxWidth);
      
      lines.forEach((line: string) => {
        if (yPos > 270) {
          doc.addPage();
          yPos = 20;
        }
        doc.text(line, margin, yPos);
        yPos += lineHeight;
      });
    };

    const addSection = (title: string, content: string[]) => {
      if (yPos > 250) {
        doc.addPage();
        yPos = 20;
      }
      addText(title, 12, true);
      yPos += 2;
      content.forEach(paragraph => {
        addText(paragraph, 10, false);
        yPos += 2;
      });
      yPos += sectionSpacing;
    };

    // Title
    doc.setFontSize(20);
    doc.setFont("helvetica", "bold");
    doc.text("OdyVoy Privacy Policy", margin, yPos);
    yPos += 10;

    addText("Last updated: April 9, 2026", 10, false);
    yPos += 5;

    addText("OdyVoy (\"we,\" \"us,\" or \"our\") is operated by a sole proprietor registered in Greece. We are committed to protecting your privacy and personal data in accordance with the EU General Data Protection Regulation (GDPR), the California Consumer Privacy Act (CCPA), and Greek data protection laws.", 10, false);
    yPos += sectionSpacing;

    addSection("1. Data Controller", [
      "OdyVoy acts as the data controller for the personal data collected through our Service. For any questions regarding your personal data, contact us at: info@odyvoy.com"
    ]);

    addSection("2. Information We Collect", [
      "We collect the following types of personal data:",
      "",
      "2.1 Information You Provide:",
      "• Account Information: Email address, name, and password when you create an account",
      "• Profile Data: Display name, travel preferences, and home country",
      "• Trip Data: Destinations, travel dates, packing lists, expense records, and budget information",
      "• Payment Information: Processed securely by Stripe; we do not store full payment card details",
      "• Communications: Messages you send to our support team and AI chat conversations",
      "• Search History: Recent destination searches and feature usage within the app",
      "",
      "2.2 Information Collected Automatically:",
      "• Device Information: Device type, operating system, browser type",
      "• Usage Data: Pages visited, features used, time spent on the Service, feature usage counts",
      "• IP Address: For security, fraud prevention, and approximate location",
      "• Approximate Location: Used ephemerally for weather forecasts, local tips, and contextual recommendations (not stored permanently)",
      "• Cookies: Session cookies and analytics cookies (see Section 7)",
      "• Push Notification Tokens: Device tokens for sending push notifications (if enabled)",
      "",
      "2.3 Information from Camera/Photos (with your permission):",
      "• Currency Scanner: Camera access to photograph and identify currency bills",
      "• Receipt Scanner: Camera/photo access to scan expense receipts",
      "• These images are processed in-device or temporarily and are not permanently stored on our servers"
    ]);

    addSection("3. Legal Basis for Processing", [
      "Under GDPR Article 6, we process your personal data based on:",
      "• Contract Performance: To provide the Service you requested",
      "• Legitimate Interests: To improve our Service, ensure security, and prevent fraud",
      "• Consent: For optional features like newsletter subscriptions, marketing communications, push notifications, and location-based features"
    ]);

    addSection("4. How We Use Your Information", [
      "We use your personal data to:",
      "• Provide and maintain the Service, including personalized travel recommendations",
      "• Generate AI-powered packing suggestions, travel tips, and chat responses",
      "• Process your subscription payments",
      "• Send transactional emails (account confirmations, password resets, subscription updates)",
      "• Send marketing communications (only with your consent)",
      "• Send push notifications for trip reminders and updates (if enabled)",
      "• Analyze usage patterns to improve the Service",
      "• Detect and prevent security threats and fraudulent activity",
      "• Respond to your support requests (including AI-assisted support)",
      "• Comply with legal obligations"
    ]);

    addSection("5. Information Sharing", [
      "We do not sell your personal data. We may share your information with:",
      "",
      "Service Providers:",
      "• Supabase (database and authentication hosting — EU/US)",
      "• Stripe (payment processing — US, PCI-DSS compliant)",
      "• Resend (email delivery — US)",
      "• Google Analytics (usage analytics — US, IP anonymization enabled)",
      "• Lovable AI Gateway (AI chat and smart features — processes queries, does not retain conversation data)",
      "• OpenAI (AI-assisted email support — US, data not used for training)",
      "• AccuWeather / OpenWeatherMap (weather data — destination queries only, no personal data shared)",
      "",
      "Other Sharing:",
      "• Legal Requirements: When required by law, court order, or government request",
      "• Business Transfers: In connection with a merger, acquisition, or sale of assets",
      "• With Your Consent: When you explicitly authorize sharing with third parties",
      "• Collaborative Features: When you share packing lists or expense groups with other users you invite"
    ]);

    addSection("6. Data Security", [
      "We implement appropriate technical and organizational measures to protect your personal data:",
      "• Encryption of data in transit (TLS/HTTPS) and at rest",
      "• Secure authentication with password hashing",
      "• Row-Level Security (RLS) ensuring users can only access their own data",
      "• Input sanitization to prevent injection attacks",
      "• Regular security assessments and automated vulnerability scanning",
      "• Access controls limiting employee access to personal data",
      "",
      "While we strive to protect your data, no method of transmission over the internet is 100% secure."
    ]);

    addSection("7. Cookies and Tracking Technologies", [
      "We use the following types of cookies:",
      "• Essential Cookies: Required for the Service to function (authentication, security)",
      "• Analytics Cookies: Google Analytics to understand how users interact with the Service",
      "",
      "You can manage cookie preferences through our cookie consent banner or your browser settings.",
      "Google Analytics uses IP anonymization. We do not use cookies for advertising or cross-site tracking."
    ]);

    addSection("8. Your Rights Under GDPR", [
      "As a data subject in the EU, you have the following rights:",
      "• Right of Access: Request a copy of your personal data",
      "• Right to Rectification: Correct inaccurate or incomplete data",
      "• Right to Erasure: Request deletion of your personal data (\"right to be forgotten\")",
      "• Right to Restrict Processing: Limit how we use your data",
      "• Right to Data Portability: Receive your data in a structured, machine-readable format (JSON export available in app settings)",
      "• Right to Object: Object to processing based on legitimate interests",
      "• Right to Withdraw Consent: Withdraw consent at any time for consent-based processing",
      "",
      "To exercise these rights, contact us at info@odyvoy.com. We will respond within 30 days as required by GDPR."
    ]);

    addSection("9. Your Rights Under CCPA (California Residents)", [
      "If you are a California resident, you have the following additional rights under the CCPA:",
      "• Right to Know: You can request details about the categories and specific pieces of personal information we have collected about you",
      "• Right to Delete: You can request deletion of your personal information",
      "• Right to Opt-Out of Sale: We do not sell your personal information. We do not share your data with third-party data brokers",
      "• Right to Non-Discrimination: We will not discriminate against you for exercising your CCPA rights",
      "",
      "To exercise these rights, contact us at info@odyvoy.com."
    ]);

    addSection("10. Data Retention", [
      "We retain your personal data for as long as necessary:",
      "• Account Data: Retained while your account is active, deleted within 30 days upon account deletion request",
      "• Trip Data: Retained while your account is active or as needed to provide the Service",
      "• AI Chat History: Retained while your account is active; deletable via app settings",
      "• Payment Records: Retained for 5 years as required by Greek Law 4308/2014 (Greek Accounting Standards)",
      "• Analytics Data: Aggregated and anonymized; raw data retained for up to 14 months per Google Analytics settings",
      "• Push Notification Tokens: Retained while active; automatically cleaned when app is uninstalled or notifications disabled",
      "• Camera/Photo Data: Processed in real-time and not permanently stored on our servers"
    ]);

    addSection("11. International Data Transfers", [
      "Your data may be processed outside the EEA. We ensure appropriate safeguards:",
      "• EU-US Data Privacy Framework certification (where applicable)",
      "• Standard Contractual Clauses approved by the European Commission",
      "• Adequacy decisions for transfers to countries with adequate data protection"
    ]);

    addSection("12. Children's Privacy", [
      "The Service is not intended for children under 16 years of age. We do not knowingly collect personal data from children under 16. If you believe we have collected data from a child under 16, please contact us immediately at info@odyvoy.com."
    ]);

    addSection("13. Changes to This Policy", [
      "We may update this Privacy Policy from time to time. We will notify you of material changes by:",
      "• Posting the updated policy on this page with a new \"Last updated\" date",
      "• Sending you an email notification for significant changes",
      "",
      "We encourage you to review this policy periodically."
    ]);

    addSection("14. Contact Us", [
      "For questions about this Privacy Policy or to exercise your data protection rights:",
      "",
      "Email & Data Protection Inquiries: info@odyvoy.com",
      "Business Name: Eirini Maria Oikonomou (Sole Proprietor)",
      "Business Location: Konitsis 39, Vrilissia, 15238, Greece",
      "",
      "We aim to respond to all inquiries within 30 days."
    ]);

    doc.save("OdyVoy-Privacy-Policy.pdf");
    toast.success("Privacy Policy PDF downloaded successfully");
  };

  return (
    <>
      <Helmet>
        <title>Privacy Policy | OdyVoy - Smart Travel Planning</title>
        <meta name="description" content="OdyVoy Privacy Policy - Learn how we collect, use, and protect your personal information when you use our travel planning services." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://odyvoy.com/privacy" />
        <meta property="og:title" content="OdyVoy Privacy Policy — How We Protect Your Data" />
        <meta property="og:description" content="How OdyVoy collects, uses, and safeguards your personal data under GDPR and Greek law." />
        <meta property="og:url" content="https://odyvoy.com/privacy" />
        <meta property="og:type" content="article" />
      </Helmet>
      
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8 max-w-4xl">
          <div className="flex items-center justify-between mb-6">
            <Link to="/">
              <Button variant="ghost">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Home
              </Button>
            </Link>
            <Button onClick={handleDownloadPDF} variant="outline">
              <Download className="mr-2 h-4 w-4" />
              Download PDF
            </Button>
          </div>
          
          <h1 className="text-4xl font-bold text-foreground mb-2">OdyVoy Privacy Policy</h1>
          
          <div className="prose prose-lg dark:prose-invert max-w-none space-y-6">
            <p className="text-muted-foreground text-lg">
              Last updated: April 9, 2026
            </p>
            
            <p className="text-muted-foreground">
              OdyVoy ("we," "us," or "our") is operated by a sole proprietor registered in Greece. We are committed to protecting your privacy and personal data in accordance with the EU General Data Protection Regulation (GDPR), the California Consumer Privacy Act (CCPA), and Greek data protection laws.
            </p>
            
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">1. Data Controller</h2>
              <p className="text-muted-foreground">
                OdyVoy acts as the data controller for the personal data collected through our Service. For any questions regarding your personal data, contact us at:{" "}
                <a href="mailto:info@odyvoy.com" className="text-primary hover:underline">info@odyvoy.com</a>
              </p>
            </section>
            
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">2. Information We Collect</h2>
              <p className="text-muted-foreground">
                We collect the following types of personal data:
              </p>
              
              <h3 className="text-xl font-medium text-foreground mt-4">2.1 Information You Provide</h3>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li><strong>Account Information:</strong> Email address, name, and password when you create an account</li>
                <li><strong>Profile Data:</strong> Display name, travel preferences, and home country</li>
                <li><strong>Trip Data:</strong> Destinations, travel dates, packing lists, expense records, and budget information</li>
                <li><strong>Payment Information:</strong> Processed securely by Stripe; we do not store full payment card details</li>
                <li><strong>Communications:</strong> Messages you send to our support team and AI chat conversations</li>
                <li><strong>Search History:</strong> Recent destination searches and feature usage within the app</li>
              </ul>

              <h3 className="text-xl font-medium text-foreground mt-4">2.2 Information Collected Automatically</h3>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li><strong>Device Information:</strong> Device type, operating system, browser type</li>
                <li><strong>Usage Data:</strong> Pages visited, features used, time spent on the Service, feature usage counts</li>
                <li><strong>IP Address:</strong> For security, fraud prevention, and approximate location</li>
                <li><strong>Approximate Location:</strong> Used ephemerally for weather forecasts, local tips, and contextual recommendations (not stored permanently)</li>
                <li><strong>Cookies:</strong> Session cookies and analytics cookies (see Section 7)</li>
                <li><strong>Push Notification Tokens:</strong> Device tokens for sending push notifications (if enabled)</li>
              </ul>

              <h3 className="text-xl font-medium text-foreground mt-4">2.3 Information from Camera/Photos (with your permission)</h3>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li><strong>Currency Scanner:</strong> Camera access to photograph and identify currency bills</li>
                <li><strong>Receipt Scanner:</strong> Camera/photo access to scan expense receipts</li>
              </ul>
              <p className="text-muted-foreground text-sm">
                These images are processed in-device or temporarily and are not permanently stored on our servers.
              </p>
            </section>
            
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">3. Legal Basis for Processing</h2>
              <p className="text-muted-foreground">
                Under GDPR Article 6, we process your personal data based on:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li><strong>Contract Performance:</strong> To provide the Service you requested</li>
                <li><strong>Legitimate Interests:</strong> To improve our Service, ensure security, and prevent fraud</li>
                <li><strong>Consent:</strong> For optional features like newsletter subscriptions, marketing communications, push notifications, and location-based features</li>
              </ul>
            </section>
            
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">4. How We Use Your Information</h2>
              <p className="text-muted-foreground">
                We use your personal data to:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Provide and maintain the Service, including personalized travel recommendations</li>
                <li>Generate AI-powered packing suggestions, travel tips, and chat responses</li>
                <li>Process your subscription payments</li>
                <li>Send transactional emails (account confirmations, password resets, subscription updates)</li>
                <li>Send marketing communications (only with your consent)</li>
                <li>Send push notifications for trip reminders and updates (if enabled)</li>
                <li>Analyze usage patterns to improve the Service</li>
                <li>Detect and prevent security threats and fraudulent activity</li>
                <li>Respond to your support requests (including AI-assisted support)</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>
            
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">5. Information Sharing</h2>
              <p className="text-muted-foreground">
                We do not sell your personal data. We may share your information with:
              </p>
              
              <h3 className="text-xl font-medium text-foreground mt-4">Service Providers</h3>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Supabase (database and authentication hosting — EU/US)</li>
                <li>Stripe (payment processing — US, PCI-DSS compliant)</li>
                <li>Resend (email delivery — US)</li>
                <li>Google Analytics (usage analytics — US, IP anonymization enabled)</li>
                <li>Lovable AI Gateway (AI chat and smart features — processes queries, does not retain conversation data)</li>
                <li>OpenAI (AI-assisted email support — US, data not used for training)</li>
                <li>AccuWeather / OpenWeatherMap (weather data — destination queries only, no personal data shared)</li>
              </ul>
              
              <h3 className="text-xl font-medium text-foreground mt-4">Other Sharing</h3>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li><strong>Legal Requirements:</strong> When required by law, court order, or government request</li>
                <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
                <li><strong>With Your Consent:</strong> When you explicitly authorize sharing with third parties</li>
                <li><strong>Collaborative Features:</strong> When you share packing lists or expense groups with other users you invite</li>
              </ul>
            </section>
            
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">6. Data Security</h2>
              <p className="text-muted-foreground">
                We implement appropriate technical and organizational measures to protect your personal data:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Encryption of data in transit (TLS/HTTPS) and at rest</li>
                <li>Secure authentication with password hashing</li>
                <li>Row-Level Security (RLS) ensuring users can only access their own data</li>
                <li>Input sanitization to prevent injection attacks</li>
                <li>Regular security assessments and automated vulnerability scanning</li>
                <li>Access controls limiting employee access to personal data</li>
              </ul>
              <p className="text-muted-foreground">
                While we strive to protect your data, no method of transmission over the internet is 100% secure.
              </p>
            </section>
            
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">7. Cookies and Tracking Technologies</h2>
              <p className="text-muted-foreground">
                We use the following types of cookies:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li><strong>Essential Cookies:</strong> Required for the Service to function (authentication, security)</li>
                <li><strong>Analytics Cookies:</strong> Google Analytics to understand how users interact with the Service</li>
              </ul>
              <p className="text-muted-foreground">
                You can manage cookie preferences through our cookie consent banner or your browser settings.
              </p>
              <p className="text-muted-foreground">
                Google Analytics uses IP anonymization. We do not use cookies for advertising or cross-site tracking.
              </p>
            </section>
            
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">8. Your Rights Under GDPR</h2>
              <p className="text-muted-foreground">
                As a data subject in the EU, you have the following rights:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li><strong>Right of Access:</strong> Request a copy of your personal data</li>
                <li><strong>Right to Rectification:</strong> Correct inaccurate or incomplete data</li>
                <li><strong>Right to Erasure:</strong> Request deletion of your personal data ("right to be forgotten")</li>
                <li><strong>Right to Restrict Processing:</strong> Limit how we use your data</li>
                <li><strong>Right to Data Portability:</strong> Receive your data in a structured, machine-readable format (JSON export available in app settings)</li>
                <li><strong>Right to Object:</strong> Object to processing based on legitimate interests</li>
                <li><strong>Right to Withdraw Consent:</strong> Withdraw consent at any time for consent-based processing</li>
              </ul>
              <p className="text-muted-foreground">
                To exercise these rights, contact us at <a href="mailto:info@odyvoy.com" className="text-primary hover:underline">info@odyvoy.com</a>. We will respond within 30 days as required by GDPR.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">9. Your Rights Under CCPA (California Residents)</h2>
              <p className="text-muted-foreground">
                If you are a California resident, you have the following additional rights under the CCPA:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li><strong>Right to Know:</strong> You can request details about the categories and specific pieces of personal information we have collected about you</li>
                <li><strong>Right to Delete:</strong> You can request deletion of your personal information</li>
                <li><strong>Right to Opt-Out of Sale:</strong> We do not sell your personal information. We do not share your data with third-party data brokers</li>
                <li><strong>Right to Non-Discrimination:</strong> We will not discriminate against you for exercising your CCPA rights</li>
              </ul>
              <p className="text-muted-foreground">
                To exercise these rights, contact us at <a href="mailto:info@odyvoy.com" className="text-primary hover:underline">info@odyvoy.com</a>.
              </p>
            </section>
            
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">10. Data Retention</h2>
              <p className="text-muted-foreground">
                We retain your personal data for as long as necessary:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li><strong>Account Data:</strong> Retained while your account is active, deleted within 30 days upon account deletion request</li>
                <li><strong>Trip Data:</strong> Retained while your account is active or as needed to provide the Service</li>
                <li><strong>AI Chat History:</strong> Retained while your account is active; deletable via app settings</li>
                <li><strong>Payment Records:</strong> Retained for 5 years as required by Greek Law 4308/2014 (Greek Accounting Standards)</li>
                <li><strong>Analytics Data:</strong> Aggregated and anonymized; raw data retained for up to 14 months per Google Analytics settings</li>
                <li><strong>Push Notification Tokens:</strong> Retained while active; automatically cleaned when app is uninstalled or notifications disabled</li>
                <li><strong>Camera/Photo Data:</strong> Processed in real-time and not permanently stored on our servers</li>
              </ul>
            </section>
            
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">11. International Data Transfers</h2>
              <p className="text-muted-foreground">
                Your data may be processed outside the European Economic Area (EEA) by our service providers. When this occurs, we ensure appropriate safeguards are in place:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>EU-US Data Privacy Framework certification (where applicable)</li>
                <li>Standard Contractual Clauses approved by the European Commission</li>
                <li>Adequacy decisions for transfers to countries with adequate data protection</li>
              </ul>
            </section>
            
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">12. Children's Privacy</h2>
              <p className="text-muted-foreground">
                The Service is not intended for children under 16 years of age. We do not knowingly collect personal data from children under 16. If you believe we have collected data from a child under 16, please contact us immediately at <a href="mailto:info@odyvoy.com" className="text-primary hover:underline">info@odyvoy.com</a>.
              </p>
            </section>
            
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">13. Changes to This Policy</h2>
              <p className="text-muted-foreground">
                We may update this Privacy Policy from time to time. We will notify you of material changes by:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Posting the updated policy on this page with a new "Last updated" date</li>
                <li>Sending you an email notification for significant changes</li>
              </ul>
              <p className="text-muted-foreground">
                We encourage you to review this policy periodically.
              </p>
            </section>
            
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">14. Contact Us</h2>
              <p className="text-muted-foreground">
                For questions about this Privacy Policy or to exercise your data protection rights:
              </p>
              <p className="text-muted-foreground">
                <strong>Email & Data Protection Inquiries:</strong>{" "}
                <a href="mailto:info@odyvoy.com" className="text-primary hover:underline">info@odyvoy.com</a><br />
                <strong>Business Name:</strong> Eirini Maria Oikonomou (Sole Proprietor)<br />
                <strong>Business Location:</strong> Konitsis 39, Vrilissia, 15238, Greece
              </p>
              <p className="text-muted-foreground">
                We aim to respond to all inquiries within 30 days.
              </p>
            </section>
          </div>
        </div>
      </div>
    </>
  );
};

export default Privacy;
